import React, { useState, useEffect, useCallback, useMemo } from 'react';
import {
  Box,
  Typography,
  Button,
  TextField,
  Switch,
  FormControlLabel,
  Chip,
  IconButton,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Paper,
  CircularProgress,
  FormControl,
  InputLabel,
  LinearProgress,
  Fade,
  useTheme,
  alpha,
  Accordion,
  AccordionSummary,
  AccordionDetails,
  Select,
  MenuItem,
  Alert,
  Tooltip,
  InputAdornment,
  Divider
} from '@mui/material';
import AddIcon from '@mui/icons-material/Add';
import EditIcon from '@mui/icons-material/Edit';
import DeleteIcon from '@mui/icons-material/Delete';
import ContentCopyIcon from '@mui/icons-material/ContentCopy';
import SearchIcon from '@mui/icons-material/Search';
import TuneIcon from '@mui/icons-material/Tune';
import LayersIcon from '@mui/icons-material/Layers';
import PublicIcon from '@mui/icons-material/Public';
import Inventory2Icon from '@mui/icons-material/Inventory2';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import CheckCircleOutlineIcon from '@mui/icons-material/CheckCircleOutline';
import { toast } from 'react-toastify';
import { api } from '../services/AuthService';
import { useAuth } from '../services/AuthService';
import ProductOptionEditor from './ProductOptionEditor';

function ProductOptionsManager() {
  const theme = useTheme();
  const [groups, setGroups] = useState([]);
  const [templates, setTemplates] = useState([]);
  const [products, setProducts] = useState([]);
  const [lanchonetes, setLanchonetes] = useState([]);
  const [loading, setLoading] = useState(true);
  const [groupDialogOpen, setGroupDialogOpen] = useState(false);
  const [templateDialogOpen, setTemplateDialogOpen] = useState(false);
  const [editingGroup, setEditingGroup] = useState(null);
  const [editingTemplate, setEditingTemplate] = useState(null);
  const [expandedGroup, setExpandedGroup] = useState(null);
  const [searchTerm, setSearchTerm] = useState('');
  const [scopeFilter, setScopeFilter] = useState('all');
  const [typeFilter, setTypeFilter] = useState('all');
  const { isAdminMaster, getLanchoneteId } = useAuth();

  const [groupFormData, setGroupFormData] = useState({
    name: '',
    type: 'single',
    required: false,
    min_selections: 1,
    max_selections: 1,
    product_id: '',
    lanchonete_id: ''
  });

  const [templateFormData, setTemplateFormData] = useState({
    name: '',
    type: 'single',
    required: false,
    min_selections: 1,
    max_selections: 1,
    lanchonete_id: ''
  });

  const loadData = useCallback(async () => {
    try {
      setLoading(true);
      
      if (isAdminMaster()) {
        // Admin Master vê todos produtos e lanchonetes
        const [groupsResponse, templatesResponse, productsResponse, lanchonetesResponse] = await Promise.all([
          api.get('/product-options/groups'),
          api.get('/product-options/templates'),
          api.get('/produtos'),
          api.get('/lanchonetes')
        ]);
        setGroups(groupsResponse.data);
        setTemplates(templatesResponse.data);
        setProducts(productsResponse.data);
        setLanchonetes(lanchonetesResponse.data);
      } else {
        const lanchoneteId = getLanchoneteId();
        console.log('🔍 DEBUG FRONTEND: lanchoneteId:', lanchoneteId);
        console.log('🔍 DEBUG FRONTEND: Fazendo chamadas API...');

        if (!lanchoneteId) {
          setGroups([]);
          setTemplates([]);
          setProducts([]);
          toast.error('Usuario sem lanchonete associada');
          return;
        }
        
        try {
          console.log('🔍 DEBUG FRONTEND: Iniciando requisição /groups...');
          const groupsResponse = await api.get(`/product-options/groups?lanchonete_id=${lanchoneteId}`);
          console.log('🔍 DEBUG FRONTEND: groupsResponse:', groupsResponse);
          console.log('🔍 DEBUG FRONTEND: groupsResponse.data:', groupsResponse.data);
          
          console.log('🔍 DEBUG FRONTEND: Iniciando requisição /templates...');
          const templatesResponse = await api.get(`/product-options/templates?lanchonete_id=${lanchoneteId}`);
          console.log('🔍 DEBUG FRONTEND: templatesResponse:', templatesResponse);
          
          console.log('🔍 DEBUG FRONTEND: Iniciando requisição /produtos...');
          const productsResponse = await api.get(`/produtos?lanchonete_id=${lanchoneteId}`);
          console.log('🔍 DEBUG FRONTEND: productsResponse:', productsResponse);
          
          setGroups(groupsResponse.data);
          setTemplates(templatesResponse.data);
          setProducts(productsResponse.data);
        } catch (apiError) {
          console.error('🔍 DEBUG FRONTEND: Erro nas chamadas API:', apiError);
          console.error('🔍 DEBUG FRONTEND: apiError.response:', apiError.response);
          console.error('🔍 DEBUG FRONTEND: apiError.status:', apiError.response?.status);
          console.error('🔍 DEBUG FRONTEND: apiError.data:', apiError.response?.data);
          throw apiError;
        }
        
        // Set lanchonete_id nos formulários
        setGroupFormData(prev => ({ ...prev, lanchonete_id: lanchoneteId }));
        setTemplateFormData(prev => ({ ...prev, lanchonete_id: lanchoneteId }));
      }
    } catch (error) {
      toast.error('Erro ao carregar dados');
      console.error('Erro ao carregar dados:', error);
    } finally {
      setLoading(false);
    }
  }, [isAdminMaster, getLanchoneteId]);

  useEffect(() => {
    loadData();
  }, [loadData]);

  const handleOpenGroupDialog = (group = null) => {
    setEditingGroup(group);
    if (group) {
      setGroupFormData({
        name: group.name,
        type: group.type,
        required: group.required,
        min_selections: group.min_selections,
        max_selections: group.max_selections,
        product_id: group.product_id || '',
        lanchonete_id: group.lanchonete_id || ''
      });
    } else {
      setGroupFormData({
        name: '',
        type: 'single',
        required: false,
        min_selections: 1,
        max_selections: 1,
        product_id: '',
        lanchonete_id: getLanchoneteId() || ''
      });
    }
    setGroupDialogOpen(true);
  };

  const handleOpenTemplateDialog = (template = null) => {
    setEditingTemplate(template);
    if (template) {
      setTemplateFormData({
        name: template.name,
        type: template.type,
        required: template.required,
        min_selections: template.min_selections,
        max_selections: template.max_selections,
        lanchonete_id: template.lanchonete_id || ''
      });
    } else {
      setTemplateFormData({
        name: '',
        type: 'single',
        required: false,
        min_selections: 1,
        max_selections: 1,
        lanchonete_id: getLanchoneteId() || ''
      });
    }
    setTemplateDialogOpen(true);
  };

  const handleCloseGroupDialog = () => {
    setGroupDialogOpen(false);
    setEditingGroup(null);
  };

  const handleCloseTemplateDialog = () => {
    setTemplateDialogOpen(false);
    setEditingTemplate(null);
  };

  const handleGroupInputChange = (e) => {
    const { name, value, type, checked } = e.target;
    setGroupFormData(prev => ({
      ...prev,
      [name]: type === 'checkbox' ? checked : value,
      ...(name === 'type' && value === 'single' ? { min_selections: 1, max_selections: 1 } : {})
    }));
  };

  const handleTemplateInputChange = (e) => {
    const { name, value, type, checked } = e.target;
    setTemplateFormData(prev => ({
      ...prev,
      [name]: type === 'checkbox' ? checked : value,
      ...(name === 'type' && value === 'single' ? { min_selections: 1, max_selections: 1 } : {})
    }));
  };

  const handleCreateGroup = async () => {
    try {
      const groupData = {
        ...groupFormData,
        required: groupFormData.required ? 1 : 0,
        // Quando vazio, o grupo vira global para toda a lanchonete.
        product_id: groupFormData.product_id ? parseInt(groupFormData.product_id, 10) : null,
        lanchonete_id: isAdminMaster() ? groupFormData.lanchonete_id : getLanchoneteId()
      };
      
      console.log('Group data sendo enviado:', groupData);
      
      await api.post('/product-options/groups', groupData);
      toast.success('Grupo criado com sucesso!');
      setGroupDialogOpen(false);
      setGroupFormData({
        name: '',
        type: 'single',
        required: false,
        min_selections: 1,
        max_selections: 1,
        product_id: '',
        lanchonete_id: getLanchoneteId()
      });
      loadData();
    } catch (error) {
      console.log('Erro completo ao criar grupo:', error);
      console.log('Resposta do servidor:', error.response?.data);
      toast.error(error.response?.data?.error || 'Erro ao salvar grupo');
    }
  };

  const handleSaveGroup = async (e) => {
    e.preventDefault();
    if (Number(groupFormData.min_selections) > Number(groupFormData.max_selections)) {
      toast.warning('O mínimo de escolhas não pode ser maior que o máximo');
      return;
    }
    
    try {
      if (editingGroup) {
        await api.put(`/product-options/groups/${editingGroup.id}`, groupFormData);
        toast.success('Grupo atualizado com sucesso!');
      } else {
        await handleCreateGroup();
      }
      
      handleCloseGroupDialog();
      loadData();
    } catch (error) {
      toast.error('Erro ao salvar grupo');
      console.error('Erro ao salvar grupo:', error);
    }
  };

  const handleCreateTemplate = async () => {
    try {
      const templateData = {
        ...templateFormData,
        required: templateFormData.required ? 1 : 0
      };
      
      console.log('Template data sendo enviado:', templateData);
      
      await api.post('/product-options/templates', templateData);
      toast.success('Template criado com sucesso!');
      setTemplateDialogOpen(false);
      setTemplateFormData({
        name: '',
        type: 'single',
        required: false,
        min_selections: 1,
        max_selections: 1,
        lanchonete_id: ''
      });
      loadData();
    } catch (error) {
      console.log('Erro completo ao criar template:', error);
      console.log('Resposta do servidor:', error.response?.data);
      toast.error(error.response?.data?.error || 'Erro ao criar template');
    }
  };

  const handleSaveTemplate = async (e) => {
    e.preventDefault();
    if (Number(templateFormData.min_selections) > Number(templateFormData.max_selections)) {
      toast.warning('O mínimo de escolhas não pode ser maior que o máximo');
      return;
    }
    
    try {
      if (editingTemplate) {
        await api.put(`/product-options/templates/${editingTemplate.id}`, templateFormData);
        toast.success('Template atualizado com sucesso!');
      } else {
        await handleCreateTemplate();
      }
      
      handleCloseTemplateDialog();
      loadData();
    } catch (error) {
      toast.error('Erro ao salvar template');
      console.error('Erro ao salvar template:', error);
    }
  };

  const handleDeleteGroup = async (id) => {
    if (window.confirm('Tem certeza que deseja deletar este grupo?')) {
      try {
        await api.delete(`/product-options/groups/${id}`);
        toast.success('Grupo deletado com sucesso!');
        loadData();
      } catch (error) {
        toast.error('Erro ao deletar grupo');
        console.error('Erro ao deletar grupo:', error);
      }
    }
  };

  const handleDeleteTemplate = async (id) => {
    if (window.confirm('Tem certeza que deseja deletar este template?')) {
      try {
        await api.delete(`/product-options/templates/${id}`);
        toast.success('Template deletado com sucesso!');
        loadData();
      } catch (error) {
        toast.error('Erro ao deletar template');
        console.error('Erro ao deletar template:', error);
      }
    }
  };

  const handleApplyTemplate = async (templateId, productId) => {
    if (!productId) {
      toast.warning('Selecione um produto primeiro');
      return;
    }

    try {
      await api.post(`/product-options/templates/${templateId}/apply/${productId}`);
      toast.success('Template aplicado com sucesso!');
      loadData();
    } catch (error) {
      toast.error('Erro ao aplicar template');
      console.error('Erro ao aplicar template:', error);
    }
  };

  const handleToggleAccordion = (groupId) => {
    setExpandedGroup(expandedGroup === groupId ? null : groupId);
  };

  const handleApplyGroupToAllProducts = async (group) => {
    if (!group?.id) return;
    if (!window.confirm(`Aplicar o grupo "${group.name}" em todos os produtos da lanchonete?`)) {
      return;
    }

    try {
      const response = await api.post(`/product-options/groups/${group.id}/apply-all-products`);
      toast.success(response?.data?.message || 'Grupo aplicado em todos os produtos!');
      loadData();
    } catch (error) {
      toast.error(error.response?.data?.error || 'Erro ao aplicar grupo em todos os produtos');
      console.error('Erro ao aplicar grupo em todos os produtos:', error);
    }
  };

  const getProductName = useCallback((productId) => {
    if (!productId) return 'Todos os produtos da lanchonete';
    const product = products.find(p => p.id === productId);
    return product ? product.name : 'Produto não encontrado';
  }, [products]);

  const isGlobalGroup = useCallback((group) =>
    group && (group.product_id == null || group.product_id === ''), []);

  const filteredGroups = useMemo(() => {
    const normalizedSearch = searchTerm.trim().toLocaleLowerCase('pt-BR');
    return groups.filter((group) => {
      const productName = getProductName(group.product_id);
      const matchesSearch = !normalizedSearch ||
        String(group.name || '').toLocaleLowerCase('pt-BR').includes(normalizedSearch) ||
        String(productName || '').toLocaleLowerCase('pt-BR').includes(normalizedSearch);
      const matchesScope = scopeFilter === 'all' ||
        (scopeFilter === 'global' ? isGlobalGroup(group) : !isGlobalGroup(group));
      const matchesType = typeFilter === 'all' || group.type === typeFilter;
      return matchesSearch && matchesScope && matchesType;
    });
  }, [groups, searchTerm, scopeFilter, typeFilter, getProductName, isGlobalGroup]);

  const globalGroupsCount = groups.filter(isGlobalGroup).length;
  const linkedGroupsCount = groups.length - globalGroupsCount;

  if (loading) {
    return (
      <Box 
        display="flex" 
        justifyContent="center" 
        alignItems="center" 
        minHeight="100vh"
        sx={{ 
          background: `linear-gradient(135deg, ${theme.palette.primary.main} 0%, ${theme.palette.primary.dark} 100%)`,
          flexDirection: 'column',
          gap: 3
        }}
      >
        <Fade in={true}>
          <Box sx={{ textAlign: 'center' }}>
            <CircularProgress 
              size={80} 
              thickness={4}
              sx={{ 
                color: theme.palette.primary.contrastText,
                mb: 2
              }} 
            />
            <Typography variant="h6" color="inherit">
              Carregando opções de produtos...
            </Typography>
            <Typography variant="body2" color="inherit" sx={{ opacity: 0.8 }}>
              Preparando suas informações
            </Typography>
          </Box>
        </Fade>
        <LinearProgress 
          sx={{ 
            width: '200px',
            bgcolor: alpha(theme.palette.primary.contrastText, 0.2),
            '& .MuiLinearProgress-bar': {
              bgcolor: theme.palette.primary.contrastText
            }
          }} 
        />
      </Box>
    );
  }

  return (
    <Box sx={{ pb: 6 }}>
      <Paper
        elevation={0}
        sx={{
          p: { xs: 2.5, md: 3.5 }, mb: 3, borderRadius: 4, color: '#fff',
          background: `linear-gradient(120deg, ${theme.palette.primary.dark}, ${theme.palette.primary.main})`,
          overflow: 'hidden', position: 'relative'
        }}
      >
        <TuneIcon sx={{ position: 'absolute', right: 28, bottom: -24, fontSize: 150, opacity: 0.1 }} />
        <Box display="flex" justifyContent="space-between" alignItems={{ xs: 'stretch', sm: 'center' }} gap={2} flexDirection={{ xs: 'column', sm: 'row' }} position="relative">
          <Box>
            <Typography variant="h4" fontWeight={900}>Adicionais e opções</Typography>
            <Typography sx={{ opacity: 0.88, mt: 0.5 }}>
              Organize sabores, tamanhos, acompanhamentos e extras dos seus produtos.
            </Typography>
          </Box>
          <Button
            variant="contained" startIcon={<AddIcon />} onClick={() => handleOpenGroupDialog()}
            sx={{ bgcolor: '#fff', color: 'primary.main', fontWeight: 900, px: 2.5, py: 1.25, borderRadius: 2.5, '&:hover': { bgcolor: '#fff' } }}
          >
            Criar grupo de opções
          </Button>
        </Box>
      </Paper>

      <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr 1fr', md: 'repeat(4, 1fr)' }, gap: 2, mb: 3 }}>
        {[
          { label: 'Grupos criados', value: groups.length, icon: <LayersIcon />, color: '#E50914' },
          { label: 'Em todos os produtos', value: globalGroupsCount, icon: <PublicIcon />, color: '#0288d1' },
          { label: 'Ligados a um produto', value: linkedGroupsCount, icon: <Inventory2Icon />, color: '#7b1fa2' },
          { label: 'Modelos reutilizáveis', value: templates.length, icon: <ContentCopyIcon />, color: '#2e7d32' }
        ].map((item) => (
          <Paper key={item.label} elevation={0} sx={{ p: 2, borderRadius: 3, border: '1px solid', borderColor: 'divider' }}>
            <Box display="flex" alignItems="center" gap={1.5}>
              <Box sx={{ width: 42, height: 42, borderRadius: 2.5, display: 'grid', placeItems: 'center', color: item.color, bgcolor: alpha(item.color, 0.1) }}>{item.icon}</Box>
              <Box>
                <Typography variant="h5" fontWeight={900} lineHeight={1}>{item.value}</Typography>
                <Typography variant="caption" color="text.secondary" fontWeight={700}>{item.label}</Typography>
              </Box>
            </Box>
          </Paper>
        ))}
      </Box>

      <Paper elevation={0} sx={{ p: { xs: 2, md: 2.5 }, mb: 3, borderRadius: 3, border: '1px solid', borderColor: 'divider' }}>
        <Box display="flex" justifyContent="space-between" alignItems={{ xs: 'stretch', sm: 'center' }} gap={2} flexDirection={{ xs: 'column', sm: 'row' }} mb={2}>
          <Box>
            <Typography variant="h6" fontWeight={900}>Modelos rápidos</Typography>
            <Typography variant="body2" color="text.secondary">Configurações que você pode reaproveitar ao montar o cardápio.</Typography>
          </Box>
          <Button variant="outlined" startIcon={<AddIcon />} onClick={() => handleOpenTemplateDialog()} sx={{ borderRadius: 2 }}>
            Novo modelo
          </Button>
        </Box>
        {templates.length === 0 ? (
          <Alert severity="info" variant="outlined">Nenhum modelo criado. Eles são opcionais e ajudam a repetir configurações.</Alert>
        ) : (
          <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', md: 'repeat(2, 1fr)', xl: 'repeat(3, 1fr)' }, gap: 1.5 }}>
            {templates.map((template) => (
              <Paper key={template.id} variant="outlined" sx={{ p: 1.75, borderRadius: 2.5 }}>
                <Box display="flex" justifyContent="space-between" alignItems="flex-start" gap={1}>
                  <Box minWidth={0}>
                    <Typography fontWeight={900} noWrap>{template.name}</Typography>
                    <Box display="flex" gap={0.75} flexWrap="wrap" mt={1}>
                      <Chip size="small" label={template.type === 'single' ? 'Escolha única' : 'Múltipla escolha'} color={template.type === 'single' ? 'primary' : 'secondary'} variant="outlined" />
                      <Chip size="small" label={`${template.min_selections} a ${template.max_selections}`} variant="outlined" />
                      {template.required && <Chip size="small" label="Obrigatório" color="warning" />}
                    </Box>
                  </Box>
                  <Box display="flex">
                    <Tooltip title="Editar modelo"><IconButton size="small" onClick={() => handleOpenTemplateDialog(template)}><EditIcon fontSize="small" /></IconButton></Tooltip>
                    <Tooltip title="Excluir modelo"><IconButton size="small" color="error" onClick={() => handleDeleteTemplate(template.id)}><DeleteIcon fontSize="small" /></IconButton></Tooltip>
                  </Box>
                </Box>
              </Paper>
            ))}
          </Box>
        )}
      </Paper>

      <Paper elevation={0} sx={{ p: { xs: 2, md: 2.5 }, borderRadius: 3, border: '1px solid', borderColor: 'divider' }}>
        <Box display="flex" justifyContent="space-between" alignItems={{ xs: 'stretch', md: 'center' }} gap={2} flexDirection={{ xs: 'column', md: 'row' }} mb={2.5}>
          <Box>
            <Typography variant="h6" fontWeight={900}>Grupos dos produtos</Typography>
            <Typography variant="body2" color="text.secondary">Clique em um grupo para abrir e editar os adicionais.</Typography>
          </Box>
          <Box display="flex" gap={1} flexWrap="wrap">
            <TextField
              size="small" value={searchTerm} onChange={(event) => setSearchTerm(event.target.value)} placeholder="Buscar grupo ou produto"
              sx={{ minWidth: { xs: '100%', sm: 240 } }}
              InputProps={{ startAdornment: <InputAdornment position="start"><SearchIcon fontSize="small" /></InputAdornment> }}
            />
            <FormControl size="small" sx={{ minWidth: 150 }}>
              <InputLabel>Onde aparece</InputLabel>
              <Select value={scopeFilter} label="Onde aparece" onChange={(event) => setScopeFilter(event.target.value)}>
                <MenuItem value="all">Todos</MenuItem><MenuItem value="global">Cardápio inteiro</MenuItem><MenuItem value="product">Produto específico</MenuItem>
              </Select>
            </FormControl>
            <FormControl size="small" sx={{ minWidth: 140 }}>
              <InputLabel>Tipo</InputLabel>
              <Select value={typeFilter} label="Tipo" onChange={(event) => setTypeFilter(event.target.value)}>
                <MenuItem value="all">Todos</MenuItem><MenuItem value="single">Escolha única</MenuItem><MenuItem value="multiple">Múltipla</MenuItem>
              </Select>
            </FormControl>
          </Box>
        </Box>

        {filteredGroups.map((group) => (
          <Accordion
            key={group.id} expanded={expandedGroup === group.id} onChange={() => handleToggleAccordion(group.id)} disableGutters elevation={0}
            sx={{ mb: 1.25, border: '1px solid', borderColor: expandedGroup === group.id ? 'primary.main' : 'divider', borderRadius: '14px !important', overflow: 'hidden', '&:before': { display: 'none' } }}
          >
            <AccordionSummary expandIcon={<ExpandMoreIcon />} sx={{ px: 2, py: 0.5, '& .MuiAccordionSummary-content': { my: 1.25 } }}>
              <Box display="flex" justifyContent="space-between" alignItems="center" width="100%" gap={2}>
                <Box display="flex" alignItems="center" gap={1.5} minWidth={0}>
                  <Box sx={{ width: 42, height: 42, flexShrink: 0, borderRadius: 2.5, bgcolor: alpha(theme.palette.primary.main, 0.1), color: 'primary.main', display: 'grid', placeItems: 'center' }}>
                    {group.type === 'single' ? <CheckCircleOutlineIcon /> : <TuneIcon />}
                  </Box>
                  <Box minWidth={0}>
                    <Typography fontWeight={900} noWrap>{group.name}</Typography>
                    <Typography variant="body2" color="text.secondary" noWrap>
                      {isGlobalGroup(group) ? 'Aparece em todos os produtos' : getProductName(group.product_id)} · {group.type === 'single' ? 'Escolha única' : 'Múltipla escolha'}
                    </Typography>
                  </Box>
                </Box>
                <Box display={{ xs: 'none', sm: 'flex' }} gap={0.75} alignItems="center" flexWrap="wrap" justifyContent="flex-end">
                  <Chip size="small" label={`${group.min_selections} a ${group.max_selections}`} variant="outlined" />
                  {group.required ? <Chip size="small" label="Obrigatório" color="warning" /> : <Chip size="small" label="Opcional" variant="outlined" />}
                  {isGlobalGroup(group) && <Chip size="small" label="Cardápio inteiro" color="info" />}
                </Box>
                <Box display="flex" onClick={(event) => event.stopPropagation()}>
                  <Tooltip title="Editar configurações"><IconButton size="small" color="primary" onClick={() => handleOpenGroupDialog(group)}><EditIcon fontSize="small" /></IconButton></Tooltip>
                  <Tooltip title="Excluir grupo"><IconButton size="small" color="error" onClick={() => handleDeleteGroup(group.id)}><DeleteIcon fontSize="small" /></IconButton></Tooltip>
                </Box>
              </Box>
            </AccordionSummary>
            <AccordionDetails sx={{ p: { xs: 2, md: 2.5 }, bgcolor: alpha(theme.palette.primary.main, 0.025), borderTop: '1px solid', borderColor: 'divider' }}>
              <Box display="flex" justifyContent="space-between" alignItems={{ xs: 'stretch', sm: 'center' }} gap={1.5} flexDirection={{ xs: 'column', sm: 'row' }} mb={2}>
                <Box>
                  <Typography fontWeight={900}>Itens deste grupo</Typography>
                  <Typography variant="body2" color="text.secondary">Cadastre os nomes, preços e disponibilidade dos adicionais.</Typography>
                </Box>
                <Box display="flex" gap={1} flexWrap="wrap">
                  {!isGlobalGroup(group) && <Button size="small" variant="outlined" onClick={() => handleApplyGroupToAllProducts(group)}>Usar no cardápio inteiro</Button>}
                  {isAdminMaster() && <Button size="small" variant="outlined" startIcon={<ContentCopyIcon />} onClick={() => handleApplyTemplate(group.id, group.product_id)} disabled={!group.product_id}>Salvar modelo</Button>}
                </Box>
              </Box>
              <Divider sx={{ mb: 2 }} />
              <ProductOptionEditor groupId={group.id} onClose={loadData} />
            </AccordionDetails>
          </Accordion>
        ))}

        {filteredGroups.length === 0 && (
          <Box textAlign="center" py={7} px={2}>
            <TuneIcon sx={{ fontSize: 64, color: 'text.disabled', mb: 1 }} />
            <Typography variant="h6" fontWeight={900}>{groups.length ? 'Nenhum resultado encontrado' : 'Comece criando seu primeiro grupo'}</Typography>
            <Typography color="text.secondary" mb={2}>{groups.length ? 'Tente remover os filtros ou buscar outro nome.' : 'Exemplo: Tamanho, adicionais, molhos ou sabores.'}</Typography>
            {!groups.length && <Button variant="contained" startIcon={<AddIcon />} onClick={() => handleOpenGroupDialog()}>Criar primeiro grupo</Button>}
          </Box>
        )}
      </Paper>

      {/* Dialog de Grupo */}
      <Dialog open={groupDialogOpen} onClose={handleCloseGroupDialog} maxWidth="md" fullWidth>
        <DialogTitle sx={{ pb: 1 }}>
          <Typography variant="h5" fontWeight={900}>{editingGroup ? 'Editar grupo' : 'Criar grupo de opções'}</Typography>
          <Typography variant="body2" color="text.secondary">Defina onde o grupo aparece e quantas escolhas o cliente pode fazer.</Typography>
        </DialogTitle>
        <form onSubmit={handleSaveGroup}>
          <DialogContent>
            <Box display="flex" flexDirection="column" gap={2.5}>
              <TextField
                name="name"
                label="Nome que o cliente verá"
                placeholder="Ex.: Escolha o tamanho, Adicionais, Molhos"
                value={groupFormData.name}
                onChange={handleGroupInputChange}
                fullWidth
                required
              />

              {!groupFormData.product_id ? (
                <Alert severity="info" icon={<PublicIcon />}>
                  Este grupo aparecerá em <strong>todos os produtos</strong> da lanchonete.
                </Alert>
              ) : (
                <Alert severity="success" variant="outlined" icon={<Inventory2Icon />}>
                  Este grupo aparecerá somente no produto escolhido.
                </Alert>
              )}
              
              <FormControl fullWidth>
                <InputLabel>Onde este grupo deve aparecer?</InputLabel>
                <Select
                  name="product_id"
                  value={groupFormData.product_id}
                  onChange={handleGroupInputChange}
                  label="Onde este grupo deve aparecer?"
                >
                  <MenuItem value="">
                    Todos os produtos da lanchonete
                  </MenuItem>
                  {products
                    .filter(product => 
                      isAdminMaster() 
                        ? (!groupFormData.lanchonete_id || product.lanchonete_id === parseInt(groupFormData.lanchonete_id))
                        : product.lanchonete_id === getLanchoneteId()
                    )
                    .map((product) => (
                      <MenuItem key={product.id} value={product.id}>
                        {product.name}
                      </MenuItem>
                    ))}
                </Select>
              </FormControl>

              {/* Lanchonete_id invisível ou apenas para Admin Master */}
              {isAdminMaster() && (
                <FormControl fullWidth>
                  <InputLabel>Lanchonete</InputLabel>
                  <Select
                    name="lanchonete_id"
                    value={groupFormData.lanchonete_id}
                    onChange={handleGroupInputChange}
                    label="Lanchonete"
                    required
                  >
                    <MenuItem value="">Selecione uma lanchonete</MenuItem>
                    {lanchonetes.map((l) => (
                      <MenuItem key={l.id} value={l.id}>{l.name}</MenuItem>
                    ))}
                  </Select>
                </FormControl>
              )}
              
              <Divider />
              <Box>
                <Typography fontWeight={900} mb={1.5}>Regras de escolha</Typography>
                <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', sm: '1.4fr 1fr 1fr' }, gap: 2 }}>
                  <FormControl fullWidth>
                    <InputLabel>Como o cliente escolhe?</InputLabel>
                    <Select name="type" value={groupFormData.type} onChange={handleGroupInputChange} label="Como o cliente escolhe?">
                      <MenuItem value="single">Uma única opção</MenuItem>
                      <MenuItem value="multiple">Uma ou várias opções</MenuItem>
                    </Select>
                  </FormControl>
                  <TextField name="min_selections" label="Mínimo" type="number" value={groupFormData.min_selections} onChange={handleGroupInputChange} disabled={groupFormData.type === 'single'} inputProps={{ min: groupFormData.required ? 1 : 0 }} />
                  <TextField name="max_selections" label="Máximo" type="number" value={groupFormData.max_selections} onChange={handleGroupInputChange} disabled={groupFormData.type === 'single'} inputProps={{ min: 1 }} />
                </Box>
              </Box>

              <Paper variant="outlined" sx={{ p: 1.5, borderRadius: 2.5 }}>
                <FormControlLabel
                  sx={{ m: 0, width: '100%', justifyContent: 'space-between' }} labelPlacement="start"
                  control={<Switch name="required" checked={Boolean(groupFormData.required)} onChange={handleGroupInputChange} color="primary" />}
                  label={<Box><Typography fontWeight={900}>Escolha obrigatória</Typography><Typography variant="body2" color="text.secondary">O cliente não consegue adicionar o produto sem escolher.</Typography></Box>}
                />
              </Paper>
            </Box>
          </DialogContent>
          <DialogActions sx={{ px: 3, pb: 2.5 }}>
            <Button onClick={handleCloseGroupDialog}>
              Cancelar
            </Button>
            <Button type="submit" variant="contained" startIcon={editingGroup ? <CheckCircleOutlineIcon /> : <AddIcon />} sx={{ borderRadius: 2, px: 3 }}>
              {editingGroup ? 'Salvar alterações' : 'Criar grupo'}
            </Button>
          </DialogActions>
        </form>
      </Dialog>

      {/* Dialog de Template */}
      <Dialog open={templateDialogOpen} onClose={handleCloseTemplateDialog} maxWidth="md" fullWidth>
        <DialogTitle>
          {editingTemplate ? 'Editar Template' : 'Novo Template'}
        </DialogTitle>
        <form onSubmit={handleSaveTemplate}>
          <DialogContent>
            <Box display="flex" flexDirection="column" gap={2}>
              <TextField
                name="name"
                label="Nome do Template"
                value={templateFormData.name}
                onChange={handleTemplateInputChange}
                fullWidth
                required
              />
              
              <FormControl fullWidth>
                <InputLabel>Tipo de Seleção</InputLabel>
                <Select
                  name="type"
                  value={templateFormData.type}
                  onChange={handleTemplateInputChange}
                  label="Tipo de Seleção"
                >
                  <MenuItem value="single">Única escolha</MenuItem>
                  <MenuItem value="multiple">Múltipla escolha</MenuItem>
                </Select>
              </FormControl>
              
              <FormControlLabel
                control={
                  <Switch
                    name="required"
                    checked={templateFormData.required}
                    onChange={handleTemplateInputChange}
                    color="primary"
                  />
                }
                label="Obrigatório"
              />
              
              <TextField
                name="min_selections"
                label="Mínimo de Seleções"
                type="number"
                value={templateFormData.min_selections}
                onChange={handleTemplateInputChange}
                fullWidth
                inputProps={{ min: 1 }}
              />
              
              <TextField
                name="max_selections"
                label="Máximo de Seleções"
                type="number"
                value={templateFormData.max_selections}
                onChange={handleTemplateInputChange}
                fullWidth
                inputProps={{ min: 1 }}
              />
            </Box>
          </DialogContent>
          <DialogActions>
            <Button onClick={handleCloseTemplateDialog}>
              Cancelar
            </Button>
            <Button type="submit" variant="contained">
              {editingTemplate ? 'Atualizar' : 'Criar'}
            </Button>
          </DialogActions>
        </form>
      </Dialog>
    </Box>
  );
}

export default ProductOptionsManager;
